import ProductsApi from "@/scripts/api/products.api";
import { AbProductSpecifications } from "@/scripts/modules/ab-data-sheet/ab-product-specifications.class";
import "./abDataSheet.style.scss";
import {debounce} from "@/scripts/utils/debounce.util";
import {ProductResponseDTO} from "@shared/DTO";
import {NAMES} from "@/scripts/components/ab-data-sheet/ab-data-sheet.constants";
import {buildDataSheetTable} from "@/scripts/components/ab-data-sheet/ab-data-sheet.build-table.utils";
import {getRootLayoutClassList} from "@/scripts/components/ab-data-sheet/ab-data-sheet-functions";
import {getCompareAreaEl} from "@/scripts/components/ab-data-sheet/ab-data-sheet-build-compare-box";
import {buildCompareBoxOptions, resolveComparableProductTypes } from "@/scripts/components/ab-data-sheet/ab-data-sheet-comparison-functions";
import {createDomElementHandler} from "@/scripts/utils/dom.utils";
import {
  AbSelectChangeDetail,
  AbSelectElement,
  AbSelectSearchDetail
} from "@/scripts/components/ab-select/ab-select.types";
import {AbDataSheetUpdateEvDetail} from "@/scripts/components/ab-data-sheet/ab-data-sheet.types";
import {AbLoaderInModal} from "@/scripts/components/ab-loader/ab-loader-in-modal.class";
import {filterOptionsByTerm} from "@/scripts/utils/filterProductByNames.util";

import { getProductPurchaseCta, parseProductPurchaseScopes, ProductPurchaseScopes } from "@/scripts/components/purchase-cta/purchaseCta.model";
import { PurchaseCtaView } from "@/scripts/components/purchase-cta/purchaseCta.view";

export class AbDataSheetElement extends HTMLElement {

  private MAX_COMPARABLE_PRODUCTS = 5;

  private _productApiService = ProductsApi;

  #initialized: boolean = false;
  #resizeObserver: ResizeObserver;
  #loader: AbLoaderInModal;

  #clickEventHandler: (event: MouseEvent) => void;

  private products: ProductResponseDTO[];
  #purchaseScopes: ProductPurchaseScopes = Object.freeze({});
  #purchaseCtaViews: PurchaseCtaView[] = [];

  get purchaseScopes(): ProductPurchaseScopes {
    return this.#purchaseScopes;
  }

  set purchaseScopes(value: ProductPurchaseScopes | undefined) {
    this.#purchaseScopes = parseProductPurchaseScopes(value);
    if (this.products) {
      this.renderTable();
      this.updateLayoutDebounced();
    }
  }

  get compareAreaEl() {
    return this.querySelector(`.${NAMES.CSS_CLASSES.COMPARE_AREA}`) as HTMLElement;
  }

  get compareBoxEl() {
    const compareArea = this.compareAreaEl;
    return compareArea?.querySelector('ab-select') as AbSelectElement;
  }

  constructor() {
    super();
    this.#loader = new AbLoaderInModal();
  }

  async connectedCallback() {
    this.bindEvents();
    this.#purchaseCtaViews.forEach(view => view.connect());
    if (this.#initialized) return;
    this.#initialized = true;
    this.init();
  }

  static get observedAttributes(): string[] {
    return ['productids'];
  }

  attributeChangedCallback() {
    if (!this.isConnected || !this.#initialized) return;
    this.init();
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.#purchaseCtaViews.forEach(view => view.disconnect());
  }

  // Private Methods

  private async init() {
    const productIdsAttr = this.getAttribute('productIds');
    if (!productIdsAttr) return;
    const productIds = productIdsAttr.split(',');
    this.#loader.start();
    this.products = await this.fetchProducts(productIds);
    this.#loader.stop();
    const specifications = new AbProductSpecifications(this.products);
    this.classList.add(NAMES.CSS_CLASSES.ROOT);
    this.replaceChildren();
    if(this.products.length < this.MAX_COMPARABLE_PRODUCTS) {
      const elCompareArea = getCompareAreaEl(this.products[0].productData.productType);
      this.append(elCompareArea);
    }
    this.renderTable(specifications.items);
    this.updateLayoutDebounced?.();
    const eventDetail = { products: this.products.map(p => {
      return  {
        _id: p._id,
        name: p.productData.names?.at(0),
        type: p.productData.productType,
      }
    }) };

    if(this.getAttribute('autoOpen') === 'true') {
      this.showCompareBox();
      this.setAttribute('autoOpen', 'false'); // only once
    }

    this.dispatchEvent(
      new CustomEvent<AbDataSheetUpdateEvDetail>("ab-data-sheet-updated", {
        detail: eventDetail,
        bubbles: true,
      }),
    );

  }

  private renderTable(specs = new AbProductSpecifications(this.products).items) {
    this.#purchaseCtaViews.forEach(view => view.disconnect());
    this.#purchaseCtaViews = [];
    const oldTable = this.querySelector('.ab-data-sheet__table-wrapper');
    const elTable = buildDataSheetTable(this.products, specs, (product) => {
      const scope = Object.hasOwn(this.#purchaseScopes, product._id) ? this.#purchaseScopes[product._id] : undefined;
      const view = new PurchaseCtaView(getProductPurchaseCta(product, scope), {
        triggerClasses: ['ab-data-sheet__footer-link'],
        menuPlacement: 'viewport',
      });
      this.#purchaseCtaViews.push(view);
      return view.element;
    });
    if (oldTable) {
      const { scrollLeft, scrollTop } = oldTable;
      oldTable.replaceWith(elTable);
      elTable.scrollLeft = scrollLeft;
      elTable.scrollTop = scrollTop;
    } else {
      this.append(elTable);
    }
    if (this.isConnected) this.#purchaseCtaViews.forEach(view => view.connect());
  }

  private updateLayoutDebounced = debounce(this.updateLayout.bind(this), 200);

  private updateLayout() {
    if(!this.products || !this.#initialized ) return;
    const size = this.getBoundingClientRect().width;
    const productsCount = this.products.length;
    const isCompareBoxVisible = !!this.compareBoxEl;
    const isSticky = this.getAttribute('sticky') === 'true';
    this.classList.value = getRootLayoutClassList(size, productsCount, isCompareBoxVisible, isSticky);
    if(!this.hasAttribute('scroll-to-last')) return;
    this.removeAttribute('scroll-to-last');
    const element = this.querySelector('.ab-data-sheet__table-wrapper');
    element.scrollTo({
      left: element.scrollWidth - element.clientWidth,
      behavior: 'smooth',
    });
  }

  private onClickOnDocument(event: MouseEvent) {
    const clickedElement = event.target as HTMLElement;
    if (!clickedElement) return;

    // Click outside compare box with compare box opened (closes it)
    if( !!this.compareBoxEl && !!this.compareAreaEl && !this.compareAreaEl.contains(clickedElement)) {
      this.hideCompareBox();
      return;
    }

    if (!this.contains(clickedElement)) return;

    event.stopPropagation();

    if (clickedElement.closest(`.${NAMES.CSS_CLASSES.TRIGGER_REMOVE_ITEM}`)) {
      const productId = clickedElement.getAttribute('data-product-id');
      const newProductIds = this.products
        ?.filter((product) => product._id !== productId)
        ?.map((product) => product._id);
      this.setAttribute('productIds', newProductIds.join(','));
      return;
    }

    if (clickedElement.closest(`.${NAMES.CSS_CLASSES.TRIGGER_SHOW_COMPARE_BOX}`)) {
      this.showCompareBox();
      return;
    }

    if (clickedElement.closest(`.${NAMES.CSS_CLASSES.TRIGGER_HIDE_COMPARE_BOX}`)) {
      this.hideCompareBox();
      return;
    }

  }

  private bindEvents() {
    this.#clickEventHandler = (event: MouseEvent) => this.onClickOnDocument(event);
    document.addEventListener('click', this.#clickEventHandler);
    this.#resizeObserver = new ResizeObserver(entries => {
      this.updateLayoutDebounced();
    });
    this.#resizeObserver.observe(this);
  }

  private unbindEvents() {
    this.#resizeObserver.disconnect();
    this.#resizeObserver = null;
    document.removeEventListener('click', this.#clickEventHandler);
  }

  private async fetchProducts(productIds: string[])  {
    return Promise.all(
      productIds.map(id => this._productApiService.getProduct(id))
    ).then( (products) =>
      products.filter(p => p !== null)
    );
  }

  private async showCompareBox() {
    const samplePt = this.products?.at(0)?.productData?.productType;
    const neededProductTypes = resolveComparableProductTypes(samplePt);
    this.#loader.start();
    const allProducts = neededProductTypes.length
      ? await this._productApiService.getProductNames({ type: neededProductTypes, visibility: ["showDataSheet"] })
      : [];
    this.#loader.stop();
    const allOptions = buildCompareBoxOptions({
      allProducts,
      currentProducts: this.products,
    });
    const attrs = {
      "template-popup": "true",
      "has-shadow": "true",
      "spacing": "md",
      "font-size": "sm",
    };
    const selectEl = createDomElementHandler("ab-select", [], attrs) as AbSelectElement;
    selectEl.options = allOptions;

    selectEl.addEventListener("ab-select-search", (event: Event) => {
      const term = ((event as CustomEvent<AbSelectSearchDetail>).detail?.term ?? "").toLowerCase();
      selectEl.options = filterOptionsByTerm(term, allOptions, allProducts);
    });

    selectEl.addEventListener("ab-select-change", (event: Event) => {
      const { option } = (event as CustomEvent<AbSelectChangeDetail>).detail ?? {};
      if (!option?.id) return;
      const newProductIds = [...this.products.map(p => p._id), option.id].join(',');
      this.setAttribute('scroll-to-last', 'true');
      this.setAttribute('productIds', newProductIds);
    });

    this.compareAreaEl.appendChild(selectEl);
    this.updateLayout();
  }

  private hideCompareBox() {
    this.compareBoxEl?.remove();
    this.updateLayout();
  }

}

export function defineAbDataSheetElement() {
  const TAG_NAME = NAMES.TAG_NAME;

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, AbDataSheetElement);
  }

}
